import { Box, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useHangarsState } from '../../../../containers/HangarsStateContainer';
import { useOpsQueue } from '../../hooks/useOpsQueue';
import { useQueueActions } from '../../hooks/useQueueActions';
import { QueueHeader } from './QueueHeader';
import { QuarantineItem } from './QuarantineItem';
import { TripItem } from './TripItem';

type Props = {
  selectedHangarIds: string[];
  onAssignmentComplete: () => void;
};

/**
 * OpsQueue - displays the queue of items needing action
 * Shows quarantine items (unknown aircraft) and unassigned trips
 */
export const OpsQueue: React.FC<Props> = ({
  selectedHangarIds,
  onAssignmentComplete,
}) => {
  const { t } = useTranslation();
  const { hangars } = useHangarsState();
  const { resolveQuarantine, assignToHangar, isProcessing } = useQueueActions();
  const {
    groups,
    isLoading,
    error,
    refetch,
    filters,
    setFilters,
    totalCount,
  } = useOpsQueue();

  // Filter hangars to only those selected in the picker
  const availableHangars = React.useMemo(() => {
    if (selectedHangarIds.length === 0) return hangars;
    return hangars.filter((h) => selectedHangarIds.includes(h.id));
  }, [hangars, selectedHangarIds]);

  // Handle assignment completion - refresh queue and notify parent
  const handleAssignmentComplete = React.useCallback(() => {
    refetch();
    onAssignmentComplete();
  }, [refetch, onAssignmentComplete]);

  // Handle trip assignment
  const handleAssign = React.useCallback(
    async (tripId: string, hangarId: string) => {
      await assignToHangar(tripId, hangarId);
      handleAssignmentComplete();
    },
    [assignToHangar, handleAssignmentComplete]
  );

  // Handle quarantine resolution
  const handleResolve = React.useCallback(
    async (pendingTenantId: string, aircraftId: string) => {
      await resolveQuarantine(pendingTenantId, aircraftId);
      refetch();
    },
    [resolveQuarantine, refetch]
  );

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ p: 4 }}>
        <CircularProgress size={32} />
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack sx={{ p: 2 }}>
        <Typography color="error">{t('screens.ops.queue.loadError', 'Failed to load queue')}</Typography>
        <Typography variant="caption" color="text.secondary">
          {error.message}
        </Typography>
      </Stack>
    );
  }

  const hasQuarantine = groups.unknownAircraft.length > 0;
  const hasTrips = groups.needsRouting.length > 0;
  const isEmpty = !hasQuarantine && !hasTrips;

  return (
    <Stack sx={{ height: '100%' }}>
      {/* Queue Header with filters */}
      <QueueHeader
        filters={filters}
        onFiltersChange={setFilters}
        totalCount={totalCount}
        onRefresh={refetch}
      />

      <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        {isEmpty ? (
          <Stack alignItems="center" sx={{ p: 4 }}>
            <Typography variant="h6" color="text.secondary">
              {t('screens.ops.queue.empty.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('screens.ops.queue.empty.description')}
            </Typography>
          </Stack>
        ) : (
          <>
            {/* Quarantine Section */}
            {hasQuarantine && (
              <>
                <Typography
                  variant="overline"
                  sx={{ px: 1, color: 'warning.main', fontWeight: 600 }}
                >
                  {t('screens.ops.queue.sections.unknownAircraft')} ({groups.unknownAircraft.length})
                </Typography>
                <Stack spacing={1} sx={{ mb: 2 }}>
                  {groups.unknownAircraft.map((item) => (
                    <QuarantineItem
                      key={item.item_id}
                      item={item}
                      onResolve={handleResolve}
                      isProcessing={isProcessing}
                    />
                  ))}
                </Stack>
                {hasTrips && <Divider sx={{ my: 2 }} />}
              </>
            )}

            {/* Trips Section */}
            {hasTrips && (
              <>
                <Typography
                  variant="overline"
                  sx={{ px: 1, color: 'primary.main', fontWeight: 600 }}
                >
                  {t('screens.ops.queue.sections.needsAssignment')} ({groups.needsRouting.length})
                </Typography>
                <Stack spacing={1}>
                  {groups.needsRouting.map((item) => (
                    <TripItem
                      key={item.item_id}
                      item={item}
                      availableHangars={availableHangars}
                      onAssign={handleAssign}
                      isProcessing={isProcessing}
                    />
                  ))}
                </Stack>
              </>
            )}
          </>
        )}
      </Box>
    </Stack>
  );
};
